// eslint-disable-next-line
import cookie from 'cookie';
import axios from 'axios';
import {
  first,
  get,
  isString,
  isObject,
  pick,
  merge,
} from 'lodash';
// eslint-disable-next-line
import { useQueryParams } from 'q3-ui-queryparams';
// eslint-disable-next-line
import flat from 'flat';

const ProductRestAdapter = (req, options = {}) => {
  const defaultQuery = {};
  const { encode } = useQueryParams();
  const siteCookies = document.cookie;
  const cookies = isString(siteCookies)
    ? cookie.parse(siteCookies)
    : {};

  const instance = axios.create({
    baseURL:
      process.env.GATSBY_APP_BASE_URL ||
      'http://localhost:9000',
    timeout: 30000,
  });

  const convertObjects = (xs) =>
    isObject(xs)
      ? Object.entries(
          flat(xs, {
            safe: true,
          }),
        ).reduce((acc, [key, value]) => {
          acc[key.replace(/\./g, '~')] = value;
          return acc;
        }, {})
      : {};

  const handleResponse = (res) =>
    get(res, 'data.products', []);

  const makeHeaders = () => ({
    ...cookies,
    'X-WWW-Token': process.env.GATSBY_APP_BASE_SECRET,
    Authorization: `Bearer ${get(
      cookies,
      'X-Session-Token',
    )}`,
  });

  const withLimit = (str = '?') => {
    const limit = Number(cookies.per_page_limit || 16);
    let output = str;

    if (
      !Number.isNaN(limit) &&
      !String(str).includes('limit=')
    ) {
      if (output === '?') {
        output += `limit=${limit}`;
      } else {
        output += `&limit=${limit}`;
      }
    }

    return output;
  };

  const getBySku = async (sku) => {
    const out = first(
      await instance
        .get(`/products?sku=${sku}`, {
          headers: makeHeaders(),
        })
        .then(handleResponse),
    );

    if (!out) throw new Error('Unknown SKU');
    return out;
  };

  const getAll = async () => {
    if (isObject(options))
      Object.assign(
        defaultQuery,
        await instance
          .post(
            '/products-query-helper',
            pick(options, [
              'categorySlug',
              'searchSlug',
              'vendorSlug',
            ]),
            {
              headers: makeHeaders(),
            },
          )
          .then((d) => get(d, 'data.queryObject', {})),
      );

    const finalQueryString = withLimit(
      encode({
        ...merge(
          {},
          convertObjects(defaultQuery),
          convertObjects(req.query),
        ),
      }),
    );

    const output = await instance
      .get(`/products${finalQueryString}`, {
        headers: makeHeaders(),
      })
      .then((r) => get(r, 'data', {}));

    return {
      q: finalQueryString,
      ...output,
    };
  };

  return get(options, 'productSlug')
    ? getBySku(String(options.productSlug).toUpperCase())
    : getAll();
};

export default (alias) =>
  async ({ paramInfo, location }) => {
    try {
      let value = null;

      const query = Object.fromEntries(
        new URLSearchParams(location.search),
      );

      switch (alias) {
        case 'productSlug':
          value = { [alias]: paramInfo.id };
          break;
        case 'searchSlug':
          value = {
            [alias]: location.pathname.replace(
              '/product-searches/',
              '',
            ),
          };
          break;
        case 'categorySlug':
          value = {
            [alias]: location.pathname.replace(
              '/product-categories/',
              '',
            ),
          };
          break;
        case 'vendorSlug':
          value = {
            [alias]: location.pathname.replace(
              '/product-vendors/',
              '',
            ),
          };
          break;
        default:
          value = {};
      }

      if (
        !value &&
        !(get(query, 'search') || get(query, 'sku'))
      ) {
        return { redirect: true };
      }

      return {
        data: await ProductRestAdapter(
          { paramInfo, location, query },
          value,
        ),
      };
    } catch (error) {
      return {
        headers: {},
        props: {},
        status: 404,
      };
    }
  };
